/* @flow */
import ClientOAuth2 from 'client-oauth2';
import { action, observable, computed } from 'mobx';

import { API_PATH, CLIENT_ID, CLIENT_SECRET, GRANT_TYPE, OAUTH_PATH } from 'config';
import { browserHistory } from 'services/ServiceRegistry';
import { errorHandlerService } from '../services/ServiceRegistry';
import { getFromLocalStorage } from '../utils/LocalStorage';

type TypeTokenData = {|
  access_token: string,
  expires_in: number,
  refresh_token?: string,
  token_type?: string,
|};

type TypeOAuthResponse = {|
  accessToken: string,
  expired: () => boolean,
  refresh: () => Promise<TypeOAuthResponse>,
  client: ClientOAuth2,
  data: TypeTokenData,
  expires: Date,
  refreshToken?: string,
  tokenType?: string,
  expiresIn: any,
|};

export type ResponseOptions = {
  fullResponse?: boolean,
};

export type AuthenticationState = 'init' | 'authing' | 'unauthed' | 'authed';

export default class AuthenticationStore {
  @observable token: ?TypeOAuthResponse;
  @observable loginError: string;
  @observable role: ?string = null;
  @observable email = '';
  @observable password = '';
  @observable error: ?string;
  @observable errorResponse: Object;
  @observable _authenticationInProgress = true;

  @computed get authenticationState(): AuthenticationState {
    if (this._authenticationInProgress) {
      console.debug('AuthGetter: Authing');
      return 'authing';
    }
    if (this.token && this.token.accessToken) {
      console.debug('AuthGetter: Authed');
      return 'authed';
    }
    console.debug('AuthGetter: Unauthed');
    return 'unauthed';
  }

  client: ClientOAuth2;
  b2cToken: ?string;

  constructor() {
    this.client = new ClientOAuth2({
      accessTokenUri: OAUTH_PATH,
      clientId: CLIENT_ID,
      grantType: GRANT_TYPE,
      clientSecret: CLIENT_SECRET,
      scope: '',
    });

    const basePathComponent = window.location.pathname.split('/')[1];
    if (basePathComponent !== 'validate-jwt-token' && basePathComponent !== 'validate-magic-link') {
      Promise.all([this.initTokenFromLocal(), this.initB2cToken()]).then(() => {
        this._authenticationInProgress = false;
      });
    }
  }

  // -------------------------
  // HTTP helpers
  // -------------------------

  async fetchWithToken(url: string, config?: Object): Promise<?Response> {
    const token: ?TypeOAuthResponse = await this.getValidToken();
    const b2c_token = this.b2cToken;

    if (token) {
      const headers: Headers = config && config.headers ? config.headers : new Headers();
      headers.set('Authorization', 'Bearer ' + token.accessToken);
      headers.set('Content-Type', 'application/json');
      headers.set('X-Requested-With', 'XMLHttpRequest');
      if (b2c_token) headers.set('b2c-token', b2c_token);

      return fetch(url, {
        ...config,
        b2c_token,
        headers,
        // NOTE: Required for cross-origin requests so HttpOnly cookies are sent.
        // This enables the B2C cookie-based bridge flow to work with the backend domain.
        credentials: 'include',
      });
    }
    return null;
  }

  async fetchWithTokenHeader(url: string, config?: Object, vHeaders: *): Promise<?Response> {
    const t: ?TypeOAuthResponse = await this.getValidToken();
    if (t) {
      const headers: Headers = vHeaders || new Headers();
      headers.set('Authorization', 'Bearer ' + t.accessToken);

      return fetch(url, {
        ...config,
        headers,
        // NOTE: Keep credentials for consistency; some endpoints may rely on cookies too.
        credentials: 'include',
      });
    }
    return null;
  }

  fetchGuest(url: string, config?: Object): Promise<?Response> {
    const headers: Headers = config && config.headers ? config.headers : new Headers();
    headers.set('Content-Type', 'application/json');
    headers.set('X-Requested-With', 'XMLHttpRequest');

    return fetch(url, {
      ...config,
      headers,
      // NOTE: Required so the browser includes HttpOnly cookies in cross-origin requests.
      // Without this, the B2C token cookie set by Laravel will NOT be sent to the API domain.
      credentials: 'include',
    });
  }

  async getJsonWithToken(
    resource: string,
    config?: *,
    responseOptions?: ResponseOptions
  ): Promise<*> {
    const response: ?Response = await this.fetchWithToken(`${API_PATH}/${resource}`, config);
    return this.handleResponse(response, responseOptions);
  }

  async getJsonWithTokenHeader(resource: string, config: *, vHeaders: *): Promise<*> {
    const response: ?Response = await this.fetchWithTokenHeader(
      `${API_PATH}/${resource}`,
      config,
      vHeaders
    );
    return this.handleResponse(response);
  }

  async getJsonGuest(resource: string, config: *): Promise<*> {
    const response: ?Response = await this.fetchGuest(`${API_PATH}/${resource}`, config);
    return this.handleResponse(response);
  }

  async handleResponse(response: ?Response, options?: ResponseOptions): Object {
    if (response) {
      if (response.ok) {
        const data: * = await response.json();
        if ((data.meta && data.links) || (options && options.fullResponse)) {
          return data;
        }
        const result: * = data.data;
        return result;
      }

      switch (response.status) {
        case 401:
          this.logout();
          browserHistory.push('/login');
          break;
        case 404:
          browserHistory.push('/404', { error: 'Resource was not found' });
          return null;
        case 400:
          this.errorResponse = await response.json();
          throw new Error(`Bad Request: ${this.errorResponse}`);
        case 409: {
          this.errorResponse = await response.json();
          const conflictError = new Error(this.errorResponse.message || 'Conflict.');
          conflictError.data = this.errorResponse.data;
          throw conflictError;
        }
        case 422: {
          this.errorResponse = await response.json();
          const errors = this.errorResponse.errors || this.errorResponse.data;
          if (errors) {
            const message = errors[Object.keys(errors)[0]].toJS();
            throw new Error(message[0]);
          }
          throw new Error('Unprocessible Entity.');
        }
        case 500:
          throw new Error(
            `Server error:
              ${response.status}
              ${response.statusText}
              ${await response.text()}`
          );
        default:
          this.errorResponse = await response.json();
          this.error = this.errorResponse.message;
          throw new Error(
            `Server error:
              ${response.status}
              ${response.statusText}
              ${this.error}`
          );
      }
    }
    return null;
  }

  async getDataWithToken(resource: string, config: *): Promise<?string> {
    const response: ?Response = await this.fetchWithToken(`${API_PATH}/${resource}`, config);
    if (response) {
      if (response.ok) return response.text();

      switch (response.status) {
        case 401:
          this.logout();
          browserHistory.push('/login');
          break;
        case 404:
          browserHistory.push('/404', { error: 'Resource was not found' });
          return null;
        case 500:
          throw new Error(
            `Server error:
              ${response.status}
              ${response.statusText}
              ${await response.text()}`
          );
        default:
          throw new Error(
            `Server error:
              ${response.status}
              ${response.statusText}
              ${await response.text()}`
          );
      }
    }
    return null;
  }

  async getBlobWithToken(resource: string, config: *): Promise<?string> {
    const response: ?Response = await this.fetchWithToken(`${API_PATH}/${resource}`, config);
    if (response) {
      if (response.ok) return response.blob();

      switch (response.status) {
        case 401:
          this.logout();
          browserHistory.push('/login');
          break;
        case 404:
          browserHistory.push('/404', { error: 'Resource was not found' });
          return null;
        case 500:
          throw new Error(
            `Server error:
              ${response.status}
              ${response.statusText}
              ${await response.text()}`
          );
        default:
          throw new Error(
            `Server error:
              ${response.status}
              ${response.statusText}
              ${await response.text()}`
          );
      }
    }
    return null;
  }

  // -------------------------
  // Auth flows
  // -------------------------

  @action
  async createTokenFromMagicToken(
    accessToken: string,
    refreshToken: string,
    expiresInSeconds: number,
    data: Object
  ) {
    await this.createToken({
      access_token: accessToken,
      refresh_token: refreshToken,
      expires_in: expiresInSeconds,
      token_type: 'Bearer',
    });

    if (this.token) {
      this.token.expiresIn(expiresInSeconds);
      this.saveTokenToLocal(); // safe now
    }
  }

  @action
  logout() {
    this.token = null;
    this.email = '';
    this.password = '';
    this.removeTokenFromLocal();
  }

  @action
  async login(): Promise<boolean> {
    this._authenticationInProgress = true;
    try {
      this.token = await this.client.owner.getToken(this.email, this.password);
      this.saveTokenToLocal();
      this._authenticationInProgress = false;
      return true;
    } catch (e) {
      this.loginError = e.message;
      this._authenticationInProgress = false;
      return false;
    }
  }

  @action
  async getValidToken(): Promise<?TypeOAuthResponse> {
    if (!this.token) {
      this._authenticationInProgress = true;
      const token: ?TypeTokenData = this.getTokenFromLocal();

      if (token) {
        await this.createToken(token);
      }

      this._authenticationInProgress = false;
    } else if (this.token && this.token.expired()) {
      await this.refreshToken();
    }

    return this.token;
  }

  @action
  async refreshToken() {
    if (!this.token || typeof this.token.refresh !== 'function') return;

    this.token = await this.token.refresh();
    this.saveTokenToLocal();
  }

  saveTokenToLocal(tokenData?: TypeTokenData) {
    if (typeof Storage === 'undefined') return;

    const dataToPersist: ?TypeTokenData = tokenData || this.getSerializableTokenData(this.token);

    if (!this.isValidTokenData(dataToPersist)) {
      return;
    }

    localStorage.setItem('token', JSON.stringify(dataToPersist));
  }

  removeTokenFromLocal() {
    if (typeof Storage !== 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('user-language');
    }
  }

  getTokenFromLocal(): ?TypeTokenData {
    if (typeof Storage === 'undefined') return null;

    const token: ?string = localStorage.getItem('token');
    try {
      if (token) {
        const parsed: any = JSON.parse(token);
        if (this.isValidTokenData(parsed)) {
          return (parsed: TypeTokenData);
        }
      }
    } catch (e) {
      errorHandlerService.handleException(e);
      return null;
    }
    return null;
  }

  initTokenFromLocal(): ?TypeTokenData {
    const token = this.getTokenFromLocal();

    if (token) {
      const normalized = { ...token, expires_in: token.expires_in || 76598 };
      this.createToken(normalized);
    }
  }

  async initB2cToken() {
    const stored = await getFromLocalStorage('b2cToken');
    this.b2cToken = stored && typeof stored.token === 'string' ? stored.token : null;
  }

  isUserLoggedIn(): boolean {
    return Boolean(this.token && this.token.accessToken);
  }

  @action
  async createToken(token: TypeTokenData | Object): Promise<*> {
    try {
      this.token = await this.client.createToken(
        token.access_token,
        token.refresh_token,
        token.token_type || 'Bearer'
      );

      if (this.token && token.expires_in) {
        this.token.expiresIn(token.expires_in);
      }
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this._authenticationInProgress = false;
  }

  // -------------------------
  // Private helpers (EN)
  // -------------------------

  getSerializableTokenData(tok: ?TypeOAuthResponse): ?TypeTokenData {
    if (!tok || !tok.data) return null;

    const d = tok.data;
    return {
      access_token: d.access_token,
      refresh_token: d.refresh_token,
      token_type: d.token_type || 'Bearer',
      expires_in: d.expires_in || 0,
    };
  }

  isValidTokenData(data: any): boolean {
    if (!data) return false;
    if (typeof data.access_token !== 'string' || data.access_token.length === 0) return false;
    if (typeof data.expires_in !== 'number' || data.expires_in <= 0) return false;
    return true;
  }
}
